/**
 * Step 2 - Mapeamento de colunas do CSV → campos do CLIENTE.
 *
 * Regra especial: pelo menos UM entre `cpf` e `cnpj` precisa estar mapeado.
 * O wizard pai bloqueia o botão "Próximo" enquanto a regra não é satisfeita.
 *
 * RELEASE:
 *   2026-08-23 v1.1.0 - Aviso de colunas do CSV sem destino (evita erro silencioso
 *     em que a coluna é lida mas descartada na importação).
 *   2026-05-17 v1.0.0 - Versão inicial (Fase 2)
 */

import { useMemo } from 'react';
import { ShieldAlert, Info, AlertTriangle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CUSTOMER_FIELDS, SENSITIVE_HEADERS } from './types';

interface Step2MappingCustomersProps {
  rowCount: number;
  headers: string[];
  mapping: Record<string, string>;
  onMappingChange: (next: Record<string, string>) => void;
}

export function Step2MappingCustomers({
  rowCount,
  headers,
  mapping,
  onMappingChange,
}: Step2MappingCustomersProps) {
  const ignoredHeaders = useMemo(
    () => headers.filter((h) => SENSITIVE_HEADERS.includes(h.trim().toLowerCase())),
    [headers],
  );

  // Colunas presentes no arquivo que não foram associadas a nenhum campo do
  // cliente — seus valores NÃO serão importados. Sensíveis já têm aviso próprio.
  const unmappedHeaders = useMemo(() => {
    const used = new Set(Object.values(mapping));
    return headers.filter(
      (h) => !used.has(h) && !SENSITIVE_HEADERS.includes(h.trim().toLowerCase()),
    );
  }, [headers, mapping]);

  const identitySatisfied = !!mapping.cpf || !!mapping.cnpj;

  function setField(fieldKey: string, value: string) {
    const next = { ...mapping };
    if (value === '__none__') delete next[fieldKey];
    else next[fieldKey] = value;
    onMappingChange(next);
  }


  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {rowCount.toLocaleString('pt-BR')} linhas detectadas. Mapeie cada coluna do CSV ao campo do cliente.
      </p>

      {ignoredHeaders.length > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-100">
          <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-medium">
              Colunas ignoradas por segurança: {ignoredHeaders.map((h) => `"${h}"`).join(', ')}
            </p>
            <p className="text-xs opacity-90">
              Campos como limite de crédito, status e identificadores internos não podem
              ser definidos via CSV (evita inconsistências e privilege escalation).
            </p>
          </div>
        </div>
      )}

      {!identitySatisfied && (
        <div className="flex gap-3 rounded-lg border border-blue-300/60 bg-blue-50 p-3 text-blue-900 dark:border-blue-400/30 dark:bg-blue-950/40 dark:text-blue-100">
          <Info className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Mapeie ao menos <strong>CPF</strong> ou <strong>CNPJ</strong>.</p>
            <p className="text-xs opacity-90 mt-0.5">
              Sem isso, todas as linhas serão rejeitadas como inválidas na validação.
            </p>
          </div>
        </div>
      )}

      {/* Colunas sem destino: alerta explícito para não haver perda silenciosa */}
      {unmappedHeaders.length > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-amber-900 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-100">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm space-y-1">
            <p className="font-medium">
              {unmappedHeaders.length} coluna(s) do CSV não serão importadas:{' '}
              {unmappedHeaders.map((h) => `"${h}"`).join(', ')}
            </p>
            <p className="text-xs opacity-90">
              Associe cada uma a um campo do cliente abaixo, ou siga assim se realmente
              não quiser importar esses dados.
            </p>
          </div>
        </div>
      )}



      <div className="space-y-2">
        {CUSTOMER_FIELDS.map((f) => {
          const isIdentityField = f.identityGroup === 'pfpj';
          const isMissingIdentity = isIdentityField && !identitySatisfied;
          return (
            <div key={f.key} className="grid grid-cols-2 gap-2 items-center">
              <Label className="text-sm">
                {f.label}
                {f.required && <span className="text-destructive ml-1">*</span>}
                {isMissingIdentity && <span className="text-amber-600 ml-1">*</span>}
              </Label>
              <Select
                value={mapping[f.key] || '__none__'}
                onValueChange={(v) => setField(f.key, v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— não mapear —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— não mapear —</SelectItem>
                  {headers.map((h) => (
                    <SelectItem key={h} value={h}>
                      {h}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>
    </div>
  );
}
